import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Avatar, Button, Tabs, Typography, Descriptions, Tag, Dropdown, Menu, Segmented, Form, Select, Space, Divider, Modal, message } from 'antd';
import {
    PlusCircleOutlined,
} from '@ant-design/icons';
import NotesComponent from '../../../components/Forms/NotesComponent';
import moment from 'moment';
import timeAgo from '../../../constants/functions';
import EditableFormBlock from '../../../components/Forms/EditableField';
import { MasterConfigs } from '../../../interfaces/entity';
import { constants } from '../../../constants/common';
import SmallProfileCard from '../../../components/ContactInfo/SmallProfileCard';
import StageStepper from './DealStageTracker';
import Timeline from '../Timeline';
import { getData } from '../../../actions/apiCall';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import Attachments from '../Attachments';
import Developers from './Developers';
import Projects from './Projects';
import MailsComponent from '../Mails/MailsComponent';

const { Title, Text, Link } = Typography;

const DealsDetails: React.FC<any> = ({ deal, onDealUpdate }) => {
    const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [highlighted, setHighlighted] = useState<string | null>(null);
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState<'overview' | 'timeline'>('overview');
    const dispatch = useDispatch();

    // Update form when deal data changes
    useEffect(() => {
        if (deal && form) {
            // Update form fields for both forms
            const formValues: any = {
                dealOwner: deal?.dealOwner?._id,
                currentStage: deal?.currentStage?._id,
                probability: deal?.probability,
                expectedRevenue: deal?.expectedRevenue,
                billingType: deal?.billingType,
                closingDate: deal?.closingDate ? moment(deal.closingDate) : null,
                dealName: deal?.dealName,
                amount: deal?.amount,
                leadType: deal?.leadType?._id,
                business: deal?.business?._id,
                contact: deal?.contact?._id,
                type: deal?.type,
                nextStep: deal?.nextStep,
                projectType: deal?.projectType,
                startDate: deal?.startDate ? moment(deal.startDate) : null,
                endDate: deal?.endDate ? moment(deal.endDate) : null,
                leadSource: deal?.leadSource,
                description: deal?.description,
            };

            form.setFieldsValue(formValues);

            // Update Redux cache so EditableFormBlock can access updated data
            if (deal?._id) {
                dispatch({
                    type: 'API_SUCCESS',
                    payload: { key: deal._id, data: deal },
                });
            }
        }
    }, [deal, form, dispatch]);

    const dealOwnerConfig: MasterConfigs = useMemo(() => ({
        api: () => '/deals',
        getFormColumns: () => [
            {
                label: 'Deal Owner',
                name: 'dealOwner',
                type: 'select',
                span: 24,
                commonProps: {
                    disabled: true,
                },
                render: (text) => {
                    return [text?.firstName, text?.lastName].filter(Boolean).join(' ');
                }
            },
            {
                label: 'Stage',
                name: 'currentStage',
                isCombo: true,
                type: 'select',
                render: (text) => text?.name,
                span: 24
            },
            {
                label: 'Probability (%)',
                name: 'probability',
                type: 'number',
                commonProps: {
                    disabled: true,
                },
                span: 24
            },
            {
                label: 'Expected Revenue',
                name: 'expectedRevenue',
                type: 'text',
                commonProps: {
                    disabled: true,
                },
                inputProps: {
                    prefix: 'INR ',
                    disabled: true,
                },
                render: (text) => {
                    return text ? `INR ${text}` : 'N/A';
                },
                span: 24,
            },
            {
                label: 'Billing Type',
                name: 'billingType',
                type: 'select',
                commonProps: {
                    disabled: true,
                },
                children: [
                    { name: 'Monthly', value: 'Monthly' },
                    { name: 'Hourly', value: 'Hourly' },
                ],
                render: (text) => {
                    return text || 'Monthly';
                },
                span: 24,
            },
            {
                width: 150,
                label: 'Closing Date',
                name: 'closingDate',
                type: 'date',
                render: (text) => moment(new Date(text)).format('DD MMM YYYY')
            },
        ],
        combo: [
            {
                api: deal?.leadType?._id ? `/stages?category=Deals&leadType=${deal?.leadType?._id}&andOr=and` : null,
                name: 'currentStage',
                idKey: '_id',
                render: (text) => text.name
            },
            {
                api: '/accounts',
                name: 'business',
                idKey: '_id',
                render: (text) => text.businessName
            },
        ]
    }), [deal?.leadType?._id, deal?.currentStage?._id]);

    const dealConfig: MasterConfigs = useMemo(() => ({
        api: () => '/deals',
        getFormColumns: () => [
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Deal Information</Title>
            },
            {
                label: 'Deal Owner',
                name: 'dealOwner',
                type: 'select',
                isCombo: true,
                span: 12,
                render: (text) => {
                    return [text?.firstName, text?.lastName].filter(Boolean).join(' ');
                }
            },
            {
                label: 'Billing Type',
                name: 'billingType',
                type: 'select',
                span: 12,
                children: [
                    { name: 'Monthly', value: 'Monthly' },
                    { name: 'Hourly', value: 'Hourly' },
                ],
                render: (text) => {
                    return text || 'Monthly';
                },
            },
            {
                label: 'Amount',
                name: 'amount',
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const amountRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
                            return amountRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid amount'));
                        },
                    },
                ],
                render: (text, record) => {
                    const billingType = record?.billingType || 'Monthly';
                    const label = billingType === 'Hourly' ? 'Hourly Rate' : 'Monthly Amount';
                    return text ? `INR ${text} (${label})` : 'N/A';
                },
                span: 12,
            },
            {
                label: 'Deal Name',
                name: 'dealName',
                type: 'text',
                rules: [{ required: true, message: 'Deal Name is required' }],
                span: 12,
            },
            {
                label: 'Lead Type',
                name: 'leadType',
                type: 'select',
                commonProps: {
                    disabled: true,
                },
                render: (text) => text?.name,
                span: 12,
            },
            {
                label: 'Closing Date',
                name: 'closingDate',
                type: 'date',
                span: 12,
                dateProps: {
                    format: 'DD/MM/YYYY',
                },
                render: (text) => text ? moment(new Date(text)).format('DD MMM YYYY') : 'N/A',
                rules: [{ required: true, message: 'Closing Date is required' }],
            },
            {
                label: 'Account Name',
                name: 'business',
                type: 'select',
                isCombo: true,
                render: (text) => text?.businessName,
                rules: [{ required: true, message: 'Account Name is required' }],
                span: 12,
            },
            {
                label: 'Stage',
                name: 'currentStage',
                isCombo: true,
                type: 'select',
                render: (text) => text?.name,
                rules: [{ required: true, message: 'Stage is required' }],
                span: 12,
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
            },
            {
                label: 'Contact Name',
                name: 'contact',
                type: 'select',
                render: (text) => {
                    return constants.GET_FULL_NAME(text)
                },
                isCombo: true,
                rules: [{ required: true, message: 'Contact Name is required' }],
                span: 12,
            },
            {
                label: 'Probability (%)',
                name: 'probability',
                type: 'number',
                span: 12,
            },
            {
                label: 'Type',
                name: 'type',
                type: 'select',
                span: 12,
                render: (text) => {
                    return text === 'New' ? 'New Business' : text === 'Existing' ? 'Existing Business' : 'None';
                },
                children: [
                    { name: 'None', value: 'None' },
                    { name: 'New Business', value: 'New' },
                    { name: 'Existing Business', value: 'Existing' },
                ],
            },
            {
                label: 'Expected Revenue',
                name: 'expectedRevenue',
                commonProps: {
                    disabled: true,
                },
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                },
                span: 12,
            },
            {
                label: 'Next Step',
                name: 'nextStep',
                type: 'text',
                span: 12,
            },
            {
                label: 'Project Type',
                name: 'projectType',
                type: 'select',
                span: 12,
                children: [
                    { name: 'None', value: 'None' },
                    { name: 'FixBid', value: 'FixBid' },
                    { name: 'StaffAugmentation', value: 'StaffAugmentation' },
                ],
            },
            {
                label: 'Project Start Date',
                name: 'startDate',
                type: 'date',
                dateProps: {
                    format: 'DD/MM/YYYY',
                },
                render: (text) => text ? moment(new Date(text)).format('DD MMM YYYY') : 'N/A',
                span: 12,
            },
            {
                label: 'Lead Source',
                name: 'leadSource',
                type: 'select',
                span: 12,
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                children: constants.LEAD_SOURCES
            },
            {
                label: 'Delivery Date',
                name: 'endDate',
                type: 'date',
                dateProps: {
                    format: 'DD/MM/YYYY',
                },
                span: 12,
                render: (text) => text ? moment(new Date(text)).format('DD MMM YYYY') : 'N/A',
            },
            {
                label: 'Modified By',
                name: 'updatedBy',
                commonProps: {
                    disabled: true
                },
                type: 'text',
                render: (text, data) => {
                    return <div>
                        {[text?.firstName, text?.lastName].filter(Boolean).join(' ') || 'N/A'}
                        <br />
                        <Text type="secondary" className="text-xs">
                            {moment(data?.updatedAt).format('llll')}
                        </Text>
                    </div>
                },
                span: 12,
            },
            {
                label: 'Created By',
                name: 'createdBy',
                commonProps: {
                    disabled: true
                },
                type: 'text',
                render: (text, data) => {
                    return <Space direction="vertical" size={0}>
                        <div>
                            {[text?.firstName, text?.lastName].filter(Boolean).join(' ') || 'N/A'}
                            <br />
                            <Text type="secondary" className="text-xs">
                                {moment(data?.createdAt).format('llll')}
                            </Text>
                        </div>
                    </Space>
                },
                span: 12,
            },
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Description Information</Title>
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 24,
                textAreaProps: {
                    autoSize: { minRows: 3, maxRows: 6 },
                }
            },
        ],
        combo: [
            {
                api: '/users',
                name: 'dealOwner',
                idKey: '_id',
                render: (text) => <SmallProfileCard
                    name={constants.GET_FULL_NAME(text)}
                    avatar={text?.avatar ? text?.avatar : null}
                    subTitle={null}
                />
            },
            {
                api: deal?.leadType?._id ? `/stages?category=Deals&leadType=${deal?.leadType?._id}&andOr=and` : null,
                name: 'currentStage',
                idKey: '_id',
                render: (text) => text.name
            },
            {
                api: '/accounts',
                name: 'business',
                idKey: '_id',
                render: (text) => text.businessName
            },
            {
                api: '/contacts',
                name: 'contact',
                idKey: '_id',
                render: (text) => constants.GET_FULL_NAME(text)
            },
        ],
    }), [deal?.leadType?._id, deal?.currentStage?._id, deal?.probability, deal?.expectedRevenue]);

    const sections = [
        { id: 'notes', title: 'Notes' },
        { id: 'attachments', title: 'Attachments' },
        { id: 'developer', title: 'Developer', dontShow: true },
        { id: 'projects', title: 'Projects', dontShow: true },
        { id: 'products', title: 'Products' },
        { id: 'open-activities', title: 'Open Activities' },
        { id: 'closed-activities', title: 'Closed Activities' },
        { id: 'invited-meetings', title: 'Invited Meetings' },
        { id: 'emails', title: 'Emails', dontShow: true },
        { id: 'campaigns', title: 'Campaigns' },
        { id: 'social', title: 'Social' },
    ];

    const scrollTo = (id: string) => {
        const section = sectionRefs.current[id];
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setHighlighted(id); // Highlight

            setTimeout(() => {
                setHighlighted(current => (current === id ? null : current));
            }, 1000 * 5);
        }
    };

    return (
        <div className="flex overflow-hidden">
            <div className="w-64 bg-white shadow-sm border-r  h-[calc(100vh-60px)] p-4 flex-shrink-0">
                <Text strong className="block mb-4">Related List</Text>
                <ul className="text-sm">
                    {sections.map(section => (
                        <li
                            key={section.id}
                            onClick={() => scrollTo(section.id)}
                            className="cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-xm group"
                        >
                            <div className='flex items-center justify-between h-5'>
                                {section.title}
                                <Button type='text' className='hidden group-hover:block' icon={<PlusCircleOutlined className='pt-1' />} size="small" />
                            </div>
                        </li>
                    ))}

                    <li className="text-blue-600 cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-sm">Add Related List</li>
                </ul>
                <Text strong className="block mt-6 mb-2 ">Links</Text>
                <p className="text-blue-600 cursor-pointer text-sm hover:bg-gray-100 py-2 px-2 rounded-sm">Add Link</p>
            </div>

            <div className="flex-1 overflow-y-auto h-full max-h-[calc(100vh-60px)] p-2">
                <div className="flex-1 p-2">
                    <div className='flex items-center justify-between mb-4 '>
                        <Segmented
                            options={[
                                { label: 'Overview', value: 'overview' },
                                { label: 'Timeline', value: 'timeline' },
                            ]}
                            value={activeTab}
                            onChange={value => setActiveTab(value as 'overview' | 'timeline')}
                        />
                        <div>
                            <Text className='text-sm text-gray-500'>Last Update : {timeAgo(deal?.updatedAt)}</Text>
                        </div>
                    </div>

                    <StageStepper
                        active={deal?.currentStage?._id}
                        dealId={deal?._id}
                        deal={deal}
                        onStageUpdate={async () => {
                            // Refresh deal data after stage update
                            if (deal?._id) {
                                try {
                                    const response = await dispatch(getData(`/deals/${deal._id}`));
                                    if (response?.code === 200 && response?.data) {
                                        // Update parent component's deal state
                                        if (onDealUpdate) {
                                            onDealUpdate(response.data);
                                        }
                                    }
                                } catch (error) {
                                    console.error('Error refreshing deal:', error);
                                    message.error('Failed to refresh deal data');
                                }
                            }
                        }}
                    />

                    {activeTab === 'overview' && (<div className='space-y-4'>
                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={dealOwnerConfig}
                                        id={deal?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>

                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <div className="flex items-center">
                                    <Avatar shape='circle' size={50} src={deal?.profilePhoto ? process.env.FILE_URL + deal?.profilePhoto : null}  >
                                        {deal?.contact?.firstName?.charAt(0)}
                                    </Avatar>
                                    <div className="flex-1 ml-4">
                                        <Title level={4} className="mb-0">{deal?.contact?.preName} {deal?.contact?.firstName} {deal?.contact?.lastName}</Title>
                                        <Text className="text-md">{deal?.contact?.email}</Text>
                                        <Text className="text-md block">{deal?.contact?.phone}</Text>
                                        {deal?.contact?.linkedinProfileUrl && (
                                            <Text className="text-md block">
                                                <Link href={deal?.contact?.linkedinProfileUrl} target="_blank" rel="noopener noreferrer">
                                                    LinkedIn Profile
                                                </Link>
                                            </Text>
                                        )}
                                        <Text className="text-md block">{deal?.business?.businessName}</Text>
                                        {deal?.business?.linkedinProfileUrl && (
                                            <Text className="text-md block">
                                                <Link href={deal?.business?.linkedinProfileUrl} target="_blank" rel="noopener noreferrer">
                                                    Account LinkedIn Profile
                                                </Link>
                                            </Text>
                                        )}
                                    </div>
                                </div>
                                {deal?.nextStep && <div className='flex items-center space-x-2 mr-20'>
                                    <Tag color="red">{moment(deal?.updatedAt).fromNow()}</Tag>
                                    <Text className='text-md block'>{deal?.nextStep}</Text>
                                </div>
                                }
                            </div>
                        </div>

                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={dealConfig}
                                        id={deal?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>
                        <div className='bg-white rounded-md mb-6'
                            ref={el => (sectionRefs.current['developer'] = el)}
                        >
                            <Developers refType={'Deals'} refId={deal?._id} highlighted={highlighted === 'developer'} />
                        </div>
                        <div className='bg-white rounded-md mb-6'
                            ref={el => (sectionRefs.current['projects'] = el)}
                        >
                            <Projects refType={'Projects'} refId={deal?._id} highlighted={highlighted === 'projects'} />
                        </div>
                        {sections.filter(section => !section.dontShow).map(section => (
                            <div
                                key={section.id}
                                ref={el => (sectionRefs.current[section.id] = el)}
                                className="bg-white rounded-md mb-6"
                            >
                                {!['Attachments'].includes(section.title) && <div className="flex justify-between items-center py-2 px-4 border-b">
                                    <Title level={5} className={highlighted === section.id ? 'bg-yellow-200 px-1' : ''}>{section.title}</Title>
                                    {section.title !== 'Notes' && <Button type="text" disabled>Add {section.title}</Button>}
                                </div>}
                                {!['Notes', 'Attachments'].includes(section.title) && <div className="p-4 text-gray-300 text-center">No {section.title.toLowerCase()} available</div>}
                                {section.title === 'Notes' && <NotesComponent refType={'Deals'} refId={deal?._id} />}
                                {section.title === 'Attachments' && <div className='p-4'><Attachments refType={'Deals'} refId={deal?._id} /> </div>}
                            </div>
                        ))}
                        <MailsComponent Email={deal?.email} sectionRefs={sectionRefs} highlighted={highlighted} />
                    </div>)}
                    {activeTab === 'timeline' && <Timeline type="Deals" id={deal?._id} />}
                </div>
            </div>
        </div>
    );
};

export default DealsDetails;